import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { capacitorStorage } from "@/native/storage";

export type ThemePreference = "system" | "light" | "dark";

/**
 * MV2-277 — Controls grid v2 density preference.
 *
 *   * `null`       — pick automatically: compact when site has more
 *                    than 6 devices, detailed otherwise. Default for
 *                    a new operator who hasn't expressed a preference.
 *   * `"compact"`  — fixed compact rows regardless of device count.
 *   * `"detailed"` — fixed detailed cards regardless of device count.
 */
export type ControlsDensity = "compact" | "detailed" | null;

interface UIState {
  theme: ThemePreference;
  /** MV2-865 — replace animation with instant changes, whatever the OS says. */
  reduceMotion: boolean;
  sidebarCollapsed: boolean;
  denseMode: boolean;
  controlsDensity: ControlsDensity;
  setTheme: (theme: ThemePreference) => void;
  setReduceMotion: (on: boolean) => void;
  toggleSidebar: () => void;
  setDenseMode: (dense: boolean) => void;
  setControlsDensity: (mode: ControlsDensity) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: "system",
      reduceMotion: false,
      sidebarCollapsed: false,
      denseMode: false,
      controlsDensity: null,
      setTheme: (theme) => set({ theme }),
      setReduceMotion: (reduceMotion) => set({ reduceMotion }),
      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setDenseMode: (denseMode) => set({ denseMode }),
      setControlsDensity: (controlsDensity) => set({ controlsDensity }),
    }),
    {
      name: "ui-prefs",
      version: 3,
      storage: createJSONStorage(() => capacitorStorage),
      // MV2-277 — v1 → v2 added `controlsDensity`. Existing persisted
      // state still has theme + denseMode + sidebarCollapsed; preserve
      // those and default `controlsDensity` to null (auto-pick).
      migrate: (persisted: unknown, fromVersion: number): UIState => {
        const base = (persisted as Partial<UIState>) ?? {};
        if (fromVersion < 3) {
          // v2 → v3 (MV2-865) added `reduceMotion`; v1 → v2 added
          // `controlsDensity`. Preserve what exists, default the rest.
          return {
            theme: base.theme ?? "system",
            reduceMotion: base.reduceMotion ?? false,
            sidebarCollapsed: base.sidebarCollapsed ?? false,
            denseMode: base.denseMode ?? false,
            controlsDensity: fromVersion < 2 ? null : (base.controlsDensity ?? null),
            setTheme: () => {},
            setReduceMotion: () => {},
            toggleSidebar: () => {},
            setDenseMode: () => {},
            setControlsDensity: () => {},
          };
        }
        return base as UIState;
      },
    },
  ),
);
